import { createMutationObserver, throttleWithTrailing } from '../../utils.js';
import { RemoveNodeAttribute, SetNodeAttributeURLBased, SetCSSDataURLBased, SetNodeData, CreateTextNode, CreateElementNode, MoveNode, RemoveNode, UnbindNodes, SetNodeAttribute, AdoptedSSInsertRuleURLBased, AdoptedSSAddOwner, SetNodeSlot, } from '../messages.gen.js';
import { isRootNode, isTextNode, isElementNode, isSVGElement, isUseElement, hasTag, isCommentNode, } from '../guards.js';
import { inlineRemoteCss } from './cssInliner.js';
import { nextID } from '../../modules/constructedStyleSheets.js';
import { SanitizeLevel } from '../sanitizer.js';
const iconCache = {};
const svgUrlCache = {};
export async function parseUseEl(useElement, mode, domParser) {
    try {
        const href = useElement.getAttribute('xlink:href') || useElement.getAttribute('href');
        if (!href) {
            console.debug('Openreplay: xlink:href or href not found on <use>.');
            return;
        }
        let [url, symbolId] = href.split('#');
        if (!url && !symbolId) {
            console.warn('Openreplay: Invalid xlink:href or href found on <use>.');
            return;
        }
        // Key the cache by url + symbol + mode: the same symbol id lives in different
        // sprite files, and different modes store different value shapes (object vs
        // string vs data-url), so keying by symbolId alone returns the wrong asset.
        // Namespace remote vs in-document sprites so a relative sprite file literally
        // named "local" can't collide with the in-document bucket.
        const cacheKey = url ? `url:${url}#${symbolId}#${mode}` : `doc:${symbolId}#${mode}`;
        if (iconCache[cacheKey]) {
            return iconCache[cacheKey];
        }
        // happens if svg spritemap is local, fastest case for us
        if (!url && symbolId) {
            const hasHashtag = href.startsWith('#');
            let escapedURL;
            if (hasHashtag) {
                escapedURL = `#${CSS.escape(href.substring(1))}`;
            }
            else {
                escapedURL = CSS.escape(href);
            }
            const symbol = document.querySelector(escapedURL);
            if (symbol) {
                const inlineSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="${symbol.getAttribute('viewBox') || '0 0 24 24'}">
          ${symbol.innerHTML}
        </svg>
      `;
                iconCache[cacheKey] = inlineSvg;
                return inlineSvg;
            }
            else {
                console.warn('Openreplay: Sprite symbol not found in the document.');
                return;
            }
        }
        let svgDoc;
        if (svgUrlCache[url]) {
            if (svgUrlCache[url] === 1) {
                await new Promise((resolve) => {
                    let tries = 0;
                    const interval = setInterval(() => {
                        if (tries > 100) {
                            clearInterval(interval);
                            resolve(false);
                        }
                        if (svgUrlCache[url] !== 1) {
                            svgDoc = svgUrlCache[url];
                            clearInterval(interval);
                            resolve(true);
                        }
                        else {
                            tries++;
                        }
                    }, 100);
                });
            }
            else {
                svgDoc = svgUrlCache[url] ?? `<svg xmlns="http://www.w3.org/2000/svg"></svg>`;
            }
        }
        else {
            svgUrlCache[url] = 1;
            try {
                const response = await fetch(url);
                const svgText = await response.text();
                svgDoc = domParser.parseFromString(svgText, 'image/svg+xml');
                svgUrlCache[url] = svgDoc;
            }
            catch (e) {
                // Clear the in-flight sentinel so a failed fetch doesn't wedge every
                // future <use> for this url into a 10s poll-then-give-up loop.
                delete svgUrlCache[url];
                throw e;
            }
        }
        if (!svgDoc) {
            return;
        }
        // @ts-ignore
        const symbol = svgDoc.getElementById(symbolId);
        if (!symbol) {
            console.debug('Openreplay: Symbol not found in SVG.');
            return '';
        }
        if (mode === 'inline') {
            const res = { paths: symbol.innerHTML, vbox: symbol.getAttribute('viewBox') || '0 0 24 24' };
            iconCache[cacheKey] = res;
            return res;
        }
        if (mode === 'svgtext') {
            const inlineSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="${symbol.getAttribute('viewBox') || '0 0 24 24'}">
          ${symbol.innerHTML}
        </svg>
      `;
            iconCache[cacheKey] = inlineSvg;
            return inlineSvg;
        }
        if (mode === 'dataurl') {
            const inlineSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="${symbol.getAttribute('viewBox') || '0 0 24 24'}">
          ${symbol.innerHTML}
        </svg>
      `;
            const encodedSvg = btoa(inlineSvg);
            const dataUrl = `data:image/svg+xml;base64,${encodedSvg}`;
            iconCache[cacheKey] = dataUrl;
            return dataUrl;
        }
        console.debug(`Openreplay: Unknown mode: ${mode}. Use "inline" or "dataurl".`);
    }
    catch (error) {
        console.error('Openreplay: Error processing <use> element:', error);
    }
}
function isIgnored(node) {
    if (isCommentNode(node)) {
        return true;
    }
    if (isTextNode(node)) {
        return false;
    }
    if (!isElementNode(node)) {
        return true;
    }
    const tag = node.tagName.toUpperCase();
    if (tag === 'LINK') {
        const rel = node.getAttribute('rel');
        const as = node.getAttribute('as');
        return !(rel?.includes('stylesheet') || as === 'style' || as === 'font');
    }
    return (tag === 'SCRIPT' || tag === 'NOSCRIPT' || tag === 'META' || tag === 'TITLE' || tag === 'BASE');
}
function isObservable(node) {
    if (isRootNode(node)) {
        return true;
    }
    return !isIgnored(node);
}
const PRESERVE_VALUE_INPUT_TYPES = new Set(['button', 'reset', 'submit', 'checkbox', 'radio']);
export function shouldSkipValueAttribute(node) {
    return hasTag(node, 'input') && !PRESERVE_VALUE_INPUT_TYPES.has(node.type);
}
/*
  TODO:
    - fix unbinding logic + send all removals first (ensure sequence is correct)
    - use document as a 0-node in the upper context (should be updated in player at first)
*/
var RecentsType;
(function (RecentsType) {
    RecentsType[RecentsType["New"] = 0] = "New";
    RecentsType[RecentsType["Removed"] = 1] = "Removed";
    RecentsType[RecentsType["Changed"] = 2] = "Changed";
})(RecentsType || (RecentsType = {}));
export default class Observer {
    constructor(app, isTopContext = false, options = {}) {
        this.app = app;
        this.isTopContext = isTopContext;
        this.commited = [];
        this.recents = new Map();
        this.indexes = [];
        this.attributesMap = new Map();
        this.textSet = new Set();
        this.slotMap = new Map();
        this.disableSprites = false;
        /**
         * this option means that, instead of using link element with href to load css,
         * we will try to parse the css text instead and send it as css rules set
         * can (and will) affect performance
         * */
        this.inlineRemoteCss = false;
        this.inlinerOptions = undefined;
        this.domParser = new DOMParser();
        /**
         * Bumped on every disconnect(). Stale async CSS-inlining callbacks from a
         * previous session (e.g. agent reload → tracker restart) compare against
         * this and bail instead of sending messages that reference vanished node ids.
         */
        this.generation = 0;
        this.throttling = true;
        this.throttledSetNodeData = throttleWithTrailing((id, parentElement, data) => this.sendNodeData(id, parentElement, data), 30);
        this.throttling = !Boolean(options.disableThrottling);
        this.disableSprites = Boolean(options.disableSprites);
        this.inlineRemoteCss = Boolean(options.inlineRemoteCss);
        this.inlinerOptions = options.inlinerOptions;
        this.observer = createMutationObserver(this.app.safe((mutations) => {
            for (const mutation of mutations) {
                // THEORY S1: app.safe() wraps this whole callback in a try/catch that
                // SILENTLY swallows — so a throw while processing one mutation aborts
                // the entire batch (the single commitNodes() below never runs) and
                // every pending node in it is lost. Isolating + logging per mutation
                // both surfaces it into the session and prevents one bad node from
                // dropping the rest of the batch.
                try {
                    // mutations order is sequential
                    const target = mutation.target;
                    const type = mutation.type;
                    if (!isObservable(target)) {
                        continue;
                    }
                    if (type === 'childList') {
                        for (let i = 0; i < mutation.removedNodes.length; i++) {
                            // Should be the same as bindTree(mutation.removedNodes[i]), but logic needs to be be untied
                            if (isObservable(mutation.removedNodes[i])) {
                                this.bindNode(mutation.removedNodes[i]);
                            }
                        }
                        for (let i = 0; i < mutation.addedNodes.length; i++) {
                            this.bindTree(mutation.addedNodes[i]);
                        }
                        continue;
                    }
                    const id = this.app.nodes.getID(target);
                    if (id === undefined) {
                        continue;
                    }
                    if (!this.recents.has(id)) {
                        this.recents.set(id, RecentsType.Changed); // TODO only when altered
                    }
                    if (type === 'attributes') {
                        const name = mutation.attributeName;
                        if (name === null) {
                            continue;
                        }
                        let attr = this.attributesMap.get(id);
                        if (attr === undefined) {
                            this.attributesMap.set(id, (attr = new Set()));
                        }
                        attr.add(name);
                        continue;
                    }
                    if (type === 'characterData') {
                        this.textSet.add(id);
                        continue;
                    }
                }
                catch (mutationErr) {
                    const t = mutation.target;
                }
            }
            this.commitNodes();
        }), this.app.options.forceNgOff);
    }
    clear() {
        this.commited.length = 0;
        this.recents.clear();
        this.indexes.length = 1;
        this.attributesMap.clear();
        this.textSet.clear();
    }
    /**
     * EXPERIMENTAL: Unbinds the removed nodes in case of iframe src change.
     *
     * right now, we're relying on nodes.maintainer
     */
    handleIframeSrcChange(iframe) {
        const oldContentDocument = iframe.contentDocument;
        if (oldContentDocument) {
            const id = this.app.nodes.getID(oldContentDocument);
            if (id !== undefined) {
                const walker = document.createTreeWalker(oldContentDocument, NodeFilter.SHOW_ELEMENT + NodeFilter.SHOW_TEXT, {
                    acceptNode: (node) => isIgnored(node) || this.app.nodes.getID(node) === undefined
                        ? NodeFilter.FILTER_REJECT
                        : NodeFilter.FILTER_ACCEPT,
                }, 
                // @ts-ignore
                false);
                let removed = 0;
                const totalBeforeRemove = this.app.nodes.getNodeCount();
                const contentDocument = iframe.contentDocument;
                const nodesUnregister = this.app.nodes.unregisterNode.bind(this.app.nodes);
                while (walker.nextNode()) {
                    if (!contentDocument.contains(walker.currentNode)) {
                        removed += 1;
                        nodesUnregister(walker.currentNode);
                    }
                }
                const removedPercent = Math.floor((removed / totalBeforeRemove) * 100);
                if (removedPercent > 30) {
                    this.app.send(UnbindNodes(removedPercent));
                }
            }
        }
    }
    sendNodeAttribute(id, node, name, value) {
        if (isSVGElement(node)) {
            if (name.startsWith('xlink:')) {
                name = name.substring(6);
            }
            if (value === null) {
                this.app.send(RemoveNodeAttribute(id, name));
                return;
            }
            if (isUseElement(node) && name === 'href' && !this.disableSprites) {
                parseUseEl(node, 'svgtext', this.domParser)
                    .then((svgData) => {
                    if (svgData) {
                        this.app.send(SetNodeAttribute(id, name, `_$OPENREPLAY_SPRITE$_${svgData}`));
                    }
                })
                    .catch((e) => {
                    console.error('Openreplay: Error parsing <use> element:', e);
                });
                return;
            }
            if (name === 'href') {
                if (value.length > 1e5) {
                    value = '';
                }
                this.app.send(SetNodeAttributeURLBased(id, name, value, this.app.getBaseHref()));
            }
            else {
                this.app.attributeSender.sendSetAttribute(id, name, value);
            }
            return;
        }
        if (name === 'src' ||
            name === 'srcset' ||
            name === 'integrity' ||
            name === 'crossorigin' ||
            name === 'autocomplete' ||
            name.substring(0, 2) === 'on') {
            return;
        }
        if (name === 'value' && shouldSkipValueAttribute(node)) {
            return;
        }
        if (value === null) {
            this.app.send(RemoveNodeAttribute(id, name));
            return;
        }
        if (name === 'style' || (name === 'href' && hasTag(node, 'link'))) {
            if ('rel' in node && node.rel === 'stylesheet' && this.inlineRemoteCss) {
                const gen = this.generation;
                setTimeout(() => {
                    inlineRemoteCss(
                    // @ts-ignore
                    node, id, this.app.getBaseHref(), nextID, (id, cssText, index, baseHref) => {
                        if (this.generation !== gen)
                            return;
                        this.app.send(AdoptedSSInsertRuleURLBased(id, cssText, index, baseHref));
                    }, (sheetId, ownerId) => {
                        if (this.generation !== gen)
                            return;
                        this.app.send(AdoptedSSAddOwner(sheetId, ownerId));
                    }, this.inlinerOptions?.forceFetch, this.inlinerOptions?.forcePlain, (cssText, fakeTextId) => {
                        if (this.generation !== gen)
                            return;
                        this.app.send(CreateTextNode(fakeTextId, id, 0));
                        this.app.send(SetCSSDataURLBased(fakeTextId, cssText, this.app.getBaseHref()));
                    });
                }, 0);
                return;
            }
            this.app.send(SetNodeAttributeURLBased(id, name, value, this.app.getBaseHref()));
            return;
        }
        if (name === 'href' || value.length > 1e5) {
            value = '';
        }
        if (['alt', 'placeholder'].includes(name) && this.app.sanitizer.privateMode) {
            value = value.replaceAll(/./g, '*');
        }
        this.app.attributeSender.sendSetAttribute(id, name, value);
    }
    sendNodeData(id, parentElement, data) {
        if (hasTag(parentElement, 'style')) {
            this.app.send(SetCSSDataURLBased(id, data, this.app.getBaseHref()));
            return;
        }
        data = this.app.sanitizer.sanitize(id, data);
        this.app.send(SetNodeData(id, data));
    }
    bindNode(node) {
        const [id, isNew] = this.app.nodes.registerNode(node);
        if (isElementNode(node) && hasTag(node, 'slot')) {
            this.app.nodes.attachNodeListener(node, 'slotchange', () => {
                const sl = node;
                sl.assignedNodes({ flatten: true }).forEach((n) => {
                    const nid = this.app.nodes.getID(n);
                    if (nid !== undefined) {
                        this.recents.set(nid, RecentsType.Changed);
                    }
                });
            });
        }
        if (isNew) {
            this.recents.set(id, RecentsType.New);
        }
        else if (this.recents.get(id) !== RecentsType.New) {
            this.recents.set(id, RecentsType.Removed);
        }
    }
    bindTree(node) {
        if (!isObservable(node)) {
            return;
        }
        this.bindNode(node);
        const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT + NodeFilter.SHOW_TEXT, {
            acceptNode: (node) => {
                // Use the Map-aware isBound() rather than the raw __openreplay_id
                // property: a node carrying a stale id from a previous observe cycle
                // must be re-accepted and re-bound here, otherwise the snapshot walk
                // silently skips it (and its subtree, e.g. a <style>'s CSS text).
                return isIgnored(node) || this.app.nodes.isBound(node)
                    ? NodeFilter.FILTER_REJECT
                    : NodeFilter.FILTER_ACCEPT;
            },
        }, 
        // @ts-ignore
        false);
        while (walker.nextNode()) {
            this.bindNode(walker.currentNode);
        }
    }
    unbindTree(node) {
        const id = this.app.nodes.unregisterNode(node);
        if (id !== undefined) {
            this.slotMap.delete(id);
        }
        if (id !== undefined && this.recents.get(id) === RecentsType.Removed) {
            // Sending RemoveNode only for parent to maintain
            this.app.send(RemoveNode(id));
            // Unregistering all the children in order to clear the memory
            const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT + NodeFilter.SHOW_TEXT, {
                acceptNode: (node) => isIgnored(node) || this.app.nodes.getID(node) === undefined
                    ? NodeFilter.FILTER_REJECT
                    : NodeFilter.FILTER_ACCEPT,
            }, 
            // @ts-ignore
            false);
            let removed = 0;
            const totalBeforeRemove = this.app.nodes.getNodeCount();
            while (walker.nextNode()) {
                removed += 1;
                this.app.nodes.unregisterNode(walker.currentNode);
            }
            const removedPercent = Math.floor((removed / totalBeforeRemove) * 100);
            if (removedPercent > 30) {
                this.app.send(UnbindNodes(removedPercent));
            }
        }
    }
    // A top-consumption function on the infinite lists test. (~1% of performance resources)
    _commitNode(id, node) {
        if (isRootNode(node)) {
            return true;
        }
        let slot = node.assignedSlot;
        const parent = node.parentNode;
        let parentID;
        // Disable parent check for the upper context HTMLHtmlElement, because it is root there... (before)
        // TODO: get rid of "special" cases (there is an issue with CreateDocument altered behaviour though)
        // TODO: Clean the logic (though now it workd fine)
        if (!hasTag(node, 'html') || !this.isTopContext) {
            if (parent === null) {
                this.unbindTree(node);
                return false;
            }
            parentID = this.app.nodes.getID(parent);
            if (parentID === undefined) {
                this.unbindTree(node);
                return false;
            }
            if (!this.commitNode(parentID)) {
                this.unbindTree(node);
                return false;
            }
            this.app.sanitizer.handleNode(id, parentID, node);
            if (this.app.sanitizer.isHidden(parentID)) {
                return false;
            }
        }
        // From here parentID === undefined if node is top context HTML node
        let sibling = node.previousSibling;
        while (sibling !== null) {
            const siblingID = this.app.nodes.getID(sibling);
            if (siblingID !== undefined) {
                this.commitNode(siblingID);
                this.indexes[id] = this.indexes[siblingID] + 1;
                break;
            }
            sibling = sibling.previousSibling;
        }
        if (sibling === null) {
            this.indexes[id] = 0;
        }
        const recentsType = this.recents.get(id);
        const isNew = recentsType === RecentsType.New;
        const index = this.indexes[id];
        if (index === undefined) {
            throw 'commitNode: missing node index';
        }
        if (isNew) {
            if (isElementNode(node)) {
                let el = node;
                if (parentID !== undefined) {
                    if (this.app.sanitizer.isHidden(id)) {
                        const width = el.clientWidth;
                        const height = el.clientHeight;
                        el = node.cloneNode();
                        el.style.width = `${width}px`;
                        el.style.height = `${height}px`;
                    }
                    if ('rel' in el && el.rel === 'stylesheet' && this.inlineRemoteCss) {
                        this.app.send(CreateElementNode(id, parentID, index, 'STYLE', false));
                    }
                    else {
                        this.app.send(CreateElementNode(id, parentID, index, el.tagName, isSVGElement(node)));
                    }
                }
                for (let i = 0; i < el.attributes.length; i++) {
                    const attr = el.attributes[i];
                    this.sendNodeAttribute(id, el, attr.nodeName, attr.value);
                }
            }
            else if (isTextNode(node)) {
                // for text node id != 0, hence parentID !== undefined and parent is Element
                this.app.send(CreateTextNode(id, parentID, index));
                if (this.throttling) {
                    this.throttledSetNodeData(id, parent, node.data);
                }
                else {
                    this.sendNodeData(id, parent, node.data);
                }
            }
            if (slot) {
                const slotID = this.app.nodes.getID(slot);
                if (slotID !== undefined) {
                    this.slotMap.set(id, slotID);
                    this.app.send(SetNodeSlot(id, slotID));
                }
            }
            return true;
        }
        if (recentsType === RecentsType.Removed && parentID !== undefined) {
            this.app.send(MoveNode(id, parentID, index));
            if (slot) {
                const slotID = this.app.nodes.getID(slot);
                if (slotID !== undefined && this.slotMap.get(id) !== slotID) {
                    this.slotMap.set(id, slotID);
                    this.app.send(SetNodeSlot(id, slotID));
                }
            }
            else if (this.slotMap.has(id)) {
                this.slotMap.delete(id);
                this.app.send(SetNodeSlot(id, 0));
            }
        }
        const attr = this.attributesMap.get(id);
        if (attr !== undefined) {
            if (!isElementNode(node)) {
                throw 'commitNode: node is not an element';
            }
            for (const name of attr) {
                this.sendNodeAttribute(id, node, name, node.getAttribute(name));
            }
        }
        if (this.textSet.has(id)) {
            if (!isTextNode(node)) {
                throw 'commitNode: node is not a text';
            }
            // for text node id != 0, hence parent is Element
            if (this.throttling) {
                this.throttledSetNodeData(id, parent, node.data);
            }
            else {
                this.sendNodeData(id, parent, node.data);
            }
        }
        return true;
    }
    commitNode(id) {
        const node = this.app.nodes.getNode(id);
        if (!node) {
            return false;
        }
        const cmt = this.commited[id];
        if (cmt !== undefined) {
            return cmt;
        }
        return (this.commited[id] = this._commitNode(id, node));
    }
    commitNodes(isStart = false) {
        let node;
        for (const [id, type] of this.recents.entries()) {
            this.commitNode(id);
            if (type === RecentsType.New && (node = this.app.nodes.getNode(id))) {
                this.app.nodes.callNodeCallbacks(node, isStart);
            }
        }
        this.clear();
    }
    // ISSSUE (nodeToBinde should be the same as node in all cases. Look at the comment about 0-node at the beginning of the file.)
    // TODO: use one observer instance for all iframes/shadowRoots (composition instiad of inheritance)
    observeRoot(node, beforeCommit, nodeToBind = node) {
        this.observer.observe(node, {
            childList: true,
            attributes: true,
            characterData: true,
            subtree: true,
            attributeOldValue: false,
            characterDataOldValue: false,
        });
        this.bindTree(nodeToBind);
        beforeCommit(this.app.nodes.getID(node));
        this.commitNodes(true);
    }
    /**
     * Re-evaluates sanitization for every tracked node in `root`'s subtree against
     * the current DOM and re-emits whatever changed. Pass the highest node you
     * changed (or the document root) so inherited levels propagate correctly.
     */
    resanitizeSubtree(root) {
        if (!isObservable(root)) {
            return;
        }
        const parent = root.parentNode;
        const parentId = parent !== null ? this.app.nodes.getID(parent) : undefined;
        const parentLevel = parentId !== undefined ? this.app.sanitizer.getLevel(parentId) : SanitizeLevel.Plain;
        this.resanitizeNode(root, parentLevel);
    }
    resanitizeNode(node, parentLevel) {
        if (isIgnored(node)) {
            return;
        }
        const id = this.app.nodes.getID(node);
        if (id === undefined) {
            // Untracked (new, or under a hidden ancestor): the live observer handles it.
            return;
        }
        const newLevel = this.app.sanitizer.computeLevel(node, parentLevel);
        const prevLevel = this.app.sanitizer.getLevel(id);
        const wasHidden = prevLevel === SanitizeLevel.Hidden;
        const willHidden = newLevel === SanitizeLevel.Hidden;
        // Crossing the hidden boundary changes the rendered structure (placeholder vs
        // real subtree), so rebuild rather than re-emit.
        if (wasHidden !== willHidden) {
            this.recreateSubtree(node);
            return;
        }
        if (willHidden) {
            return;
        }
        // Plain <-> Obscured: same structure, only leaf content changes.
        if (prevLevel !== newLevel) {
            this.app.sanitizer.setLevel(id, newLevel);
            this.reemitNode(id, node);
        }
        for (let child = node.firstChild; child !== null; child = child.nextSibling) {
            this.resanitizeNode(child, newLevel);
        }
    }
    // Destroys the node player-side and re-emits its subtree from scratch (new ids)
    // so it materializes at the freshly-computed level.
    recreateSubtree(node) {
        const id = this.app.nodes.getID(node);
        if (id === undefined) {
            return;
        }
        this.app.send(RemoveNode(id));
        this.clearSubtreeRegistration(node);
        this.bindTree(node);
        this.commitNodes();
    }
    clearSubtreeRegistration(node) {
        const clearOne = (n) => {
            const oldId = this.app.nodes.getID(n);
            if (oldId !== undefined) {
                this.app.sanitizer.setLevel(oldId, SanitizeLevel.Plain);
            }
            this.app.nodes.unregisterNode(n);
        };
        const walker = document.createTreeWalker(node, NodeFilter.SHOW_ELEMENT + NodeFilter.SHOW_TEXT, {
            acceptNode: (n) => isIgnored(n) || this.app.nodes.getID(n) === undefined
                ? NodeFilter.FILTER_REJECT
                : NodeFilter.FILTER_ACCEPT,
        }, 
        // @ts-ignore
        false);
        // Collect first, then clear: unregistering mutates the ids the walker reads.
        const subtree = [];
        while (walker.nextNode()) {
            subtree.push(walker.currentNode);
        }
        clearOne(node);
        subtree.forEach(clearOne);
    }
    reemitNode(id, node) {
        if (isTextNode(node)) {
            const parent = node.parentNode;
            if (parent !== null && isElementNode(parent)) {
                // re-runs sanitize() at the level we just set
                this.sendNodeData(id, parent, node.data);
            }
            return;
        }
        if (isElementNode(node)) {
            // inputs/images/canvas re-emit their own payload via registered callbacks
            this.app.callResanitizeCallbacks(node, id);
        }
    }
    disconnect() {
        // THEORY S3: a disconnect may discard MutationRecords still queued by the
        // browser. takeRecords() drains them — they would be discarded by
        // disconnect() below anyway, so this changes nothing functionally, it only
        // lets us SEE whether a disconnect strands un-processed DOM additions
        // (e.g. a freshly appended <head> <style>). NOTE: if this disconnect is part
        // of stop(), the surrounding buffer may be cleared and this log lost; it is
        // most reliable for mid-session re-observes (cold-start cycle / iframe).
        const pending = this.observer.takeRecords();
        if (pending.length) {
            let addedEls = 0;
            const tags = [];
            for (const m of pending) {
                for (let i = 0; i < m.addedNodes.length; i++) {
                    const n = m.addedNodes[i];
                    if (n.tagName) {
                        addedEls++;
                        if (tags.length < 10)
                            tags.push(n.tagName);
                    }
                }
            }
        }
        this.observer.disconnect();
        this.clear();
        this.throttledSetNodeData.clear();
        this.generation++;
    }
    resetThrottledSetNodeData() {
        this.throttledSetNodeData.clear();
    }
}
